/**
 * Cliente HTTP único contra el backend (REACT_APP_SERVIDOR).
 *
 * Uso en componentes (inyecta las credenciales del usuario logueado):
 *
 *   import { useApi, useSucursales } from "lib/api";
 *
 *   const { post } = useApi();
 *   const { ok, status, data } = await post("/api/informespagos", { fechadesde, fechahasta });
 *
 *   const sucursales = useSucursales(); // [{ label: "..." }, ...]
 */
import { useState, useEffect, useCallback } from "react";
import { useMaterialUIController } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";

const BASE = process.env.REACT_APP_SERVIDOR || "";

// Formatea una fecha ISO (yyyy-mm-dd, la que producen los date pickers) a
// dd/mm/yyyy para mostrarla en encabezados de reporte. Si no es ISO, la
// devuelve tal cual.
export const fechaDMY = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || "");
};

// POST JSON crudo. Nunca lanza por respuesta no-2xx ni por body no-JSON:
// siempre resuelve a { ok, status, data } para que cada pantalla decida.
export async function apiPost(path, body = {}) {
  const res = await fetch(BASE + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// post() con usuario/password del contexto ya incluidos en el body.
export function useApi() {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;

  const post = useCallback(
    (path, body = {}) => apiPost(path, { usuario: usuarioLog, password: claveLog, ...body }),
    [usuarioLog, claveLog]
  );

  return { post };
}

// Programa marcador de rolesusuarios que concede alcance multi-sucursal.
// Un rol con esta fila ve TODAS las sucursales (reportes y selectores) sin
// ser AdmG/Administrador. Contraparte de `tiene_todas_sucursales` en el
// backend (permisos.py), que es quien realmente lo hace cumplir.
export const PROGRAMA_VER_SUCURSALES = "versucursales";

// Perfil de sucursal del usuario logueado para restringir filtros de reporte.
// `esadmin` true si el tipo (roleWeb) es AdmG/Administrador o si el rol tiene
// el permiso `versucursales`; `sucursalPropia` es el código de su sucursal
// ("2-Veron" -> "2"). El backend igual fuerza la sucursal en los reportes;
// esto es para reflejarlo en los selectores.
export function usePerfilSucursal() {
  const [controller] = useMaterialUIController();
  const { roleWeb, roleDetalles } = controller;
  const esadmin =
    ["AdmG", "Administrador"].includes(String(roleWeb || "").trim()) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_VER_SUCURSALES);
  const sucursalPropia = String(localStorage.getItem("sucursal") || "").split("-")[0].trim();
  return { esadmin, sucursalPropia };
}

// Lista de sucursales al montar. `conTodos` antepone la opción "0-TODOS".
// Un usuario cuyo tipo NO es 'Administrador'/'AdmG' y cuyo rol NO tiene el
// permiso `versucursales` solo ve su propia sucursal (sin opción "Todas"): el
// backend igual la fuerza en los reportes, esto es la contraparte de UX para
// que el selector no ofrezca otras sucursales.
// `todas` salta esa restricción de rol: los selectores de DESTINO
// (transferencias de chasis/documentos) necesitan ofrecer las demás
// sucursales sin importar quién esté logueado.
export function useSucursales({ conTodos = false, todas = false } = {}) {
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleWeb, roleDetalles } = controller;
  const [sucursales, setSucursales] = useState([]);

  useEffect(() => {
    let activo = true;
    post("/api/recuperasucursales").then(({ ok, data }) => {
      if (!activo || !ok) return;
      const lista = data.sucursales || [];
      const esadmin =
        ["AdmG", "Administrador"].includes(String(roleWeb || "").trim()) ||
        tienePermisosModulo(roleDetalles, PROGRAMA_VER_SUCURSALES);
      if (!esadmin && !todas) {
        const propia = String(localStorage.getItem("sucursal") || "").split("-")[0].trim();
        const soloSuya = lista.filter(
          (s) => String(s.label || "").split("-")[0].trim() === propia
        );
        setSucursales(soloSuya.length ? soloSuya : lista);
        return;
      }
      setSucursales(conTodos ? [{ label: "0-TODOS" }, ...lista] : lista);
    });
    return () => {
      activo = false;
    };
  }, [post, conTodos, todas, roleWeb, roleDetalles]);

  return sucursales;
}
